import type { Pathname } from 'history';
import React, { useEffect, useRef } from 'react';
import BodyClassName from 'react-body-classname';
import { useDispatch, useSelector } from 'react-redux';
import { Route, Switch, useHistory, useLocation } from 'react-router';

import StarsAccount, { StarsQuiz } from '@swf/account';
import { PasswordRecoveryWizard, StarsAuth } from '@swf/auth';
import CasinoModal from '@swf/components/CasinoModal/CasinoModal';
import ErrorBoundary from '@swf/components/ErrorBoundary/ErrorBoundary';
import RedirectNotification from '@swf/components/RedirectNotification/RedirectNotification';
import { CacheControl } from '@swf/components/ResponseHeaders/ResponseHeaders';
import SecurityInvestigation from '@swf/components/SecurityInvestigation/SecurityInvestigation';
import StarsCrm from '@swf/components/StarsCrm/StarsCrm';
import StarsMPNotification from '@swf/components/StarsMPNotification/StarsMPNotification';
import NotFound from '@swf/components/StarsModal/NotFound';
import useIsomorphicLayoutEffect from '@swf/core/hooks/useIsomorphicLayoutEffect';
import type { Modal } from '@swf/core/models/modal';
import type { Dispatch } from '@swf/core/store';
import { select } from '@swf/core/store';
import { canUseDOM } from '@swf/core/utils';
import { StarsHelp } from '@swf/help';
import { StarsNotification } from '@swf/notification';
import { PokerModalRoutes } from '@swf/poker';
import BetDelay from '@swf/sports/components/Modals/BetDelay/BetDelay';

import type { ModalContextValue } from '../StarsModal/Context';
import { ModalContext } from '../StarsModal/Context';

import styles from './StarsModalRoutes.scss';

interface OwnProps {
  path?: string;
  locationState?: object;
}

type Props = OwnProps & ModalContextValue;

const StarsModalRoutes: React.FC<Props> = ({ path, locationState, setStyle }) => {
  const { location, replace } = useHistory();
  const { pathname, state } = useLocation<Modal['locationState']>();
  const prevLocationPathnameRef = useRef<Pathname>();
  const prevPathRef = useRef<Pathname>();
  const { current: prevLocationPathname } = prevLocationPathnameRef;
  const { current: prevPath } = prevPathRef;
  const isAuthenticated = useSelector(select.session.isAuthenticated);
  const {
    modal: { replace: modalReplace }
  } = useDispatch<Dispatch>();

  useIsomorphicLayoutEffect(() => {
    if (prevLocationPathname && (path !== pathname || locationState !== state)) {
      if (path) {
        if (path !== prevPath || path === pathname) {
          replace(path, locationState);
        } else {
          modalReplace([{ path: pathname, locationState: state }]);
        }
      } else {
        if (pathname !== prevLocationPathname) {
          modalReplace([{ path: pathname, locationState: state }]);
        } else {
          replace('/', locationState);
        }
      }
    }
    prevLocationPathnameRef.current = pathname;
    prevPathRef.current = path;
  }, [pathname, locationState, modalReplace, path, prevPath, prevLocationPathname, state, replace]);

  useEffect(() => {
    if (path && !prevLocationPathname) {
      replace(path, locationState);
    }
  }, [locationState, path, prevLocationPathname, replace]);

  return !(path && (!canUseDOM || prevLocationPathname)) ? null : (
    <ModalContext.Provider value={{ setStyle }}>
      <CacheControl>pubic, max-age=86400</CacheControl>
      <BodyClassName className={styles.body} />
      <ErrorBoundary fallback={<Route component={NotFound} />}>
        <Switch location={location}>
          {!isAuthenticated && (
            <Route path="/auth/password-recovery" component={PasswordRecoveryWizard} />
          )}
          <Route path="/auth" component={StarsAuth} />
          <Route path="/help/contact" component={StarsHelp} />
          <Route path="/casino" component={CasinoModal} />
          <Route path="/notifications/redirected" component={RedirectNotification} />
          <Route path="/notifications" component={StarsNotification} />
          <Route path="/mpnotifications" component={StarsMPNotification} />
          <Route path="/security-investigation" component={SecurityInvestigation} />
          <Route path="/stars-crm/:zoneID" component={StarsCrm} />
          <Route path="/homegames" component={PokerModalRoutes} />
          <Route path="/sports/bet-delay" component={BetDelay} />
          {isAuthenticated && <Route path="/account" component={StarsAccount} />}
          {isAuthenticated && <Route path="/quiz" component={StarsQuiz} />}
          {(!canUseDOM || prevLocationPathname) && <Route component={NotFound} />}
        </Switch>
      </ErrorBoundary>
    </ModalContext.Provider>
  );
};

export default StarsModalRoutes;
